/**
 * PhotoReview — review all captured photos with retake/continue options.
 */
import { useState } from 'react';
import { useAppStore } from '@/lib/store';

type PhotoSlot = 'front' | 'side';

export function PhotoReview() {
  const {
    frontPhoto,
    sidePhoto,
    rotationKeyframes,
    setStep,
    setCaptureMode,
  } = useAppStore();

  const [selectedSlot, setSelectedSlot] = useState<PhotoSlot>('front');

  const photos: { slot: PhotoSlot; label: string; photo: string | null }[] = [
    { slot: 'front', label: 'Front', photo: frontPhoto },
    { slot: 'side', label: 'Side', photo: sidePhoto },
  ];

  const hasRotation = rotationKeyframes.length > 0;

  function handleContinue() {
    // Determine capture mode based on what was captured
    if (hasRotation) {
      setCaptureMode('rotation');
      setStep('processing_rotation');
    } else if (sidePhoto) {
      setCaptureMode('side');
      // Process front first, then side
      setStep('processing');
    } else {
      setCaptureMode('quick');
      setStep('processing');
    }
  }

  function handleRetakeAll() {
    setStep('capture_session');
  }

  function handleRetakeSingle(slot: PhotoSlot) {
    if (slot === 'front') {
      setStep('capture_front');
    } else {
      setStep('capture_side');
    }
  }

  const selectedPhoto = photos.find((p) => p.slot === selectedSlot);

  return (
    <div className="screen">
      <div className="flex-1">
        {/* Header */}
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900">Review Photos</h2>
          <p className="mt-1 text-sm text-gray-500">
            Make sure your photos look good before we measure.
          </p>
        </div>

        {/* Main preview */}
        <div className="mt-4 flex justify-center">
          <div className="relative h-72 w-48 overflow-hidden rounded-2xl border-2 border-brand-200 bg-gray-100 shadow-sm">
            {selectedPhoto?.photo ? (
              <img
                src={selectedPhoto.photo}
                alt={`${selectedPhoto.label} photo`}
                className="h-full w-full object-cover"
              />
            ) : (
              <div className="flex h-full w-full items-center justify-center">
                <p className="text-sm text-gray-400">No photo</p>
              </div>
            )}
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent px-3 pb-2 pt-6">
              <p className="text-sm font-semibold text-white">{selectedPhoto?.label}</p>
            </div>
          </div>
        </div>

        {/* Thumbnail strip */}
        <div className="mt-4 flex items-center justify-center gap-3">
          {photos.map((p) => (
            <button
              key={p.slot}
              onClick={() => setSelectedSlot(p.slot)}
              className={`relative h-20 w-14 overflow-hidden rounded-lg border-2 transition-colors ${
                selectedSlot === p.slot
                  ? 'border-brand-500 shadow-md'
                  : 'border-gray-200'
              }`}
            >
              {p.photo ? (
                <img
                  src={p.photo}
                  alt={p.label}
                  className="h-full w-full object-cover"
                />
              ) : (
                <div className="flex h-full w-full items-center justify-center bg-gray-50">
                  <span className="text-[10px] text-gray-400">None</span>
                </div>
              )}
              <span className="absolute bottom-0 left-0 right-0 bg-black/40 px-1 py-0.5 text-[9px] font-medium text-white text-center">
                {p.label}
              </span>
            </button>
          ))}

          {/* Rotation keyframe count */}
          {hasRotation && (
            <div className="flex h-20 w-14 items-center justify-center rounded-lg border-2 border-gray-200 bg-gray-50">
              <div className="text-center">
                <p className="text-lg font-bold text-brand-600">{rotationKeyframes.length}</p>
                <p className="text-[9px] text-gray-400">Angles</p>
              </div>
            </div>
          )}
        </div>

        {/* Retake button for selected photo */}
        {selectedPhoto?.photo && (
          <button
            onClick={() => handleRetakeSingle(selectedSlot)}
            className="mt-3 w-full rounded-xl border border-gray-200 px-4 py-2.5 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-50"
          >
            Retake {selectedPhoto.label} Photo
          </button>
        )}

        {/* Summary */}
        <div className="mt-4 rounded-xl bg-brand-50 px-4 py-3">
          <p className="text-sm text-brand-700">
            <strong>{photos.filter((p) => p.photo).length}</strong> photos captured
            {hasRotation && `, ${rotationKeyframes.length} rotation angles`}
          </p>
        </div>
      </div>

      {/* Actions */}
      <div className="mt-6 space-y-3">
        <button onClick={handleContinue} className="btn-primary">
          Continue to Measurements
        </button>
        <button onClick={handleRetakeAll} className="btn-secondary">
          Retake All Photos
        </button>
        <button
          onClick={() => setStep('welcome')}
          className="w-full py-3 text-sm text-gray-500"
        >
          Start Over
        </button>
      </div>
    </div>
  );
}
